import React, { useState, useEffect, useRef } from "react";
import useSession from "Utils/Hooks/useSession";
import { PlaidLink } from "react-plaid-link";
import useVipApiFetcher from "Utils/Hooks/useVipApiFetcher";
import { useHistory } from "react-router-dom";
import useMode, { MODE } from "Utils/Hooks/useMode";
import useRedirectUrl from "Utils/Hooks/useRedirectUrl";
import Loading from "Components/Loading";
import { Form } from "react-bootstrap";
import useNative from "Utils/Hooks/useNative";
import useOAuthStateId from "Utils/Hooks/useOAuthStateId";
import useAppKey from "Utils/Hooks/useAppKey";
import "./OnlineEnrollment.scss";
import { Connect } from "connect-web-sdk";
import Finicity from "Components/FundingSources/Finicity";
import MaskedField from "Components/Fields/MaskedField";
import CancelButton from "Components/CancelButton";
import {
  EnrollmentSteps,
  fromOperatorDefault,
  Validators,
} from "Utils/Constants";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import lockClock from "Assets/Icons/lock-clock.svg";
import StatusUpdateCard from "Components/StatusUpdateCard";
import { Button, Checkbox, Typography } from "@pav/pav-products-design-system";
import AddAccountSuccess from "Components/AddAccountSuccess";
import { useLastEnrollmentStep } from "context/LastEnrollmentStepProvider";
import { faro, LogLevel } from "@grafana/faro-react";
import { useLimitAndProductSwitching } from "Pages/MyAccounts/useLimitAndProductSwitching";

const OnlineEnrollment = ({ plaidRedirect }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const vipApi = useVipApiFetcher();
  const mode = useMode();
  const { session, fetchSession, updateSession } = useSession();
  const [linkToken, setLinkToken] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [divStyle, setDivStyle] = useState({ display: "none" });
  const history = useHistory();
  const isNative = useNative();
  const oauth_state_id = useOAuthStateId();
  const navigateToDeposit = mode === MODE.deposit;
  const navigateToWithdraw = mode === MODE.withdraw;
  const [receivedRedirectUri, setReceivedRedirectUri] = useState(null);
  const url = useRedirectUrl();
  const appKey = useAppKey();
  const refTimeOut = useRef(false);
  const [mitekRedirect, setMitekRedirect] = useState(
    session?.mitekValidationStage !== "None" ? true : false,
  );
  const mitekLink =
    session?.mitekValidationStage !== "None" ? session?.mitekLink : null;
  const [connectUrl, setConnectUrl] = useState(null);
  const [fromOperator, setFromOperator] = useState(fromOperatorDefault);
  const identificationInfoEnabled = session?.toggles.identificationInfoEnabled;
  const [ssn, setSSN] = useState(null);
  const [showFundingPage, setShowFundingPage] = useState(null);
  const [showConfirmEnrollment, setShowConfirmEnrollment] = useState(false);
  const { setLastEnrollmentStep, LastEnrollmentStep } = useLastEnrollmentStep();
  const [acceptedEnrollmentFee, setAcceptedEnrollmentFee] = useState(false);
  const [enrollmentFeeEnabled, setEnrollmentFeeEnabled] = useState(false);
  const [enrollmentFeeAmount, setEnrollmentFeeAmount] = useState("");
  const ssnIdentificationRequired =
    !showFundingPage &&
    identificationInfoEnabled &&
    fromOperator.id?.includes(EnrollmentSteps.ID.SSN);

  const style = {
    width: "100%",
    height: "66vh",
    overflow: "hidden",
    border: "none",
    align: "center",
  };

  const { determineProductSwitchAndLimits, productType } =
    useLimitAndProductSwitching();

  // Previously inherited from useLimitAndProductSwitching, which labelled this page
  // "- My Accounts"; set it here so the page owns its own title, as Enrollment does.
  document.title = productType + " - Enroll";

  useEffect(() => {
    if (!vipApi.ready) return;
    vipApi
      .fetch("/api/enrollment")
      .then((res) => res.json())
      .then((data) => {
        if (session.toggles.identificationInfoEnabled && plaidRedirect) {
          let bankFields = {
            bank: data.bank,
          };
          setFromOperator(bankFields);
        } else {
          setFromOperator(data);
        }
        faro.api.pushEvent("Starting Online Enrollment");
      });
  }, [vipApi.ready]);

  useEffect(() => {
    const poll = async function (ms) {
      let result = await vipApi.fetch("/api/mitek/status");
      while (result.status === 202 && !refTimeOut.current) {
        updateSession(await result.json());
        await wait(ms);
        result = await vipApi.fetch("/api/mitek/status");
      }
      return result;
    };

    const wait = function (ms = 5000) {
      return new Promise((resolve) => {
        setTimeout(resolve, ms);
      });
    };
    async function fetchData() {
      if (mitekRedirect) {
        setIsLoading(false);
        const responseCode = await poll(10000);
        if (responseCode.status === 200 && !refTimeOut.current) {
          setMitekRedirect(false);
          await fetchSession(true);
        } else {
          onEnrollError("Mitek Redirect Error");
        }
      }
    }
    fetchData();
  }, [mitekRedirect]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data === "timeout") {
        refTimeOut.current = true;
      }
    };
    window.addEventListener("message", handleMessage);

    if (plaidRedirect) {
      setLinkToken(localStorage.getItem("link_token"));
      setReceivedRedirectUri(
        url
          ? url + "?oauth_state_id=" + oauth_state_id
          : window.location.href.includes("oauth_state_id=")
            ? window.location.href
            : window.location.href + "?oauth_state_id=" + oauth_state_id,
      );
      // setReceivedRedirectUri("https://cdn.plaid.com/link/v2/stable/oauth.html?oauth_state_id=" + oauth_state_id);
      setIsLoading(false);
    }

    let connectUrlReceived = false;
    (async () => {
      if (session.toggles.finicityEnabled) {
        const result = await vipApi.fetchData("/api/finicity/getUrl");
        if (result) {
          setConnectUrl(result);
          connectUrlReceived = result ? true : false;
        } else {
          faro.api.pushError(new Error("Fetch Finicity Connect URL Error"), {
            context: result,
          });
        }
        setIsLoading(false);
      }

      if (
        session.toggles.plaidEnabled &&
        !connectUrlReceived &&
        !plaidRedirect
      ) {
        let mounted = true;
        let oprUrl = "";
        if (url) {
          oprUrl = appKey
            ? "?redirectUri=" + url + "/" + appKey
            : "?redirectUri=" + url;
        } else {
          oprUrl = appKey
            ? "?redirectUri=" + window.location.href + "/" + appKey
            : "?redirectUri=" + window.location.href + "/" + session.merchantId;
        }
        try {
          await vipApi.fetch("/api/plaid/link" + oprUrl).then((res) => {
            res.json().then((data) => {
              if (!mounted) return;
              if (res.ok) {
                setLinkToken(data.linkToken);
                localStorage.setItem("link_token", data.linkToken);
              }
              if (!res.ok) {
                faro.api.pushError(new Error("Plaid Link Token Error"), {
                  context: res,
                });
              }
              setIsLoading(false);
            });
          });
        } catch (error) {
          console.error(error);
          onEnrollError("Plaid Link Token Error");
        }
        return () => {
          mounted = false;
        };
      }

      if (!session.toggles.plaidEnabled && !session.toggles.finicityEnabled) {
        setIsLoading(false);
      }
    })();

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    setEnrollmentFeeEnabled(session?.merchantEnrollmentFeeEnabled);
    setEnrollmentFeeAmount(session?.merchantEnrollmentFeeAmount?.toString());
  }, [session]);

  useEffect(() => {
    if (
      !isLoading &&
      !isNative &&
      !ssnIdentificationRequired &&
      !mitekRedirect
    ) {
      loadHandler();
    }
  }, [
    isLoading,
    isNative,
    ssnIdentificationRequired,
    connectUrl,
    mitekRedirect,
  ]);

  const openNativeLink = () => {
    faro.api.pushLog(["User selected Native link"], {
      level: LogLevel.INFO,
    });
    faro.api.pushEvent("User selected Native link");
    window.$onAndroidSuccess = function (metadata) {
      onAndroidSuccess(metadata);
    };

    window.$onAndroidError = function (error) {
      onAndroidError(error);
    };
    let message = {
      linkToken: linkToken,
    };

    var plaidTokenForAndroid = linkToken;
    if (/android/i.test(navigator.userAgent) || window?.Android) {
      handleAndroidMessage(plaidTokenForAndroid);
    }
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) || window?.webkit) {
      var iosPromise =
        window?.webkit?.messageHandlers?.NativeBridge?.postMessage(message);
      iosPromise
        .then(
          function (result) {
            onSuccessDefault(null, result);
          },
          function (err) {
            console.log(err);
          },
        )
        .finally(() => {
          //iosPromise.dispose();
        });
    }
  };

  function handleAndroidMessage(message) {
    return new Promise((resolve, reject) => {
      window?.Android?.openLinkNative(
        message,
        onAndroidSuccess.name,
        onAndroidError.name,
      );
    });
  }

  function onAndroidSuccess(metadata) {
    console.log("onAndroidSuccess called");
    console.log(metadata);
    onSuccessDefault(null, metadata);
  }

  function onAndroidError(error) {
    console.log("onAndroidError called");
    console.log(error);
  }

  const onFieldChange = (fieldName, fieldValue, isValid) => {
    if (isValid) {
      setSSN(fieldValue);
    } else {
      setSSN(undefined);
    }
  };

  const onSaveSsn = async (event) => {
    event.preventDefault();
    try {
      await vipApi
        .fetch("/api/accounts/ssn", "PUT", {
          ssn: ssn,
        })
        .then((res) => {
          if (res.ok) {
            faro.api.pushEvent("SSN saved successfully");
          }
        });
    } catch (error) {
      faro.api.pushError(
        new Error("Online Enrollment SSN Update Network Error"),
        { context: error },
      );
    }
    setShowFundingPage(true);
  };

  const onSuccessDefault = async (_, metadata) => {
    setIsLoading(true);
    faro.api.pushEvent("Enrolling with Plaid Account");
    try {
      await vipApi
        .fetch("/api/plaid/account", "PUT", {
          accountId: metadata.account_id,
          accountToken: metadata.public_token,
        })
        .then((res) => {
          res.json().then((data) => {
            setIsLoading(false);
            setLastEnrollmentStep(true);
            setShowConfirmEnrollment(true);
            if (!res.ok) {
              faro.api.pushError(new Error("Plaid Update Error"), {
                context: data,
              });
            }
          });
        });
    } catch (error) {
      faro.api.pushError(
        new Error("Online Enrollment Plaid Account Update Network Error"),
        { context: error },
      );
    }
  };

  const onSubmitCommon = async (url = "/api/plaid/enroll") => {
    setIsLoading(true);
    const response = await vipApi.fetch(url, "POST");
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      const body = await response.json();
      if (body.status) {
        if (body.status === "success") {
          onEnrollSuccess();
        } else if (body.status === "noBanks") {
          await fetchSession(true);
          history.push({ pathname: "/addAccount", state: { nobank: true } });
        } else if (body.status === "failure") {
          onFailure(body);
        } else {
          onEnrollError(body);
        }
      } else {
        onEnrollError(body);
      }
    } else {
      onEnrollError("Plaid Response is not JSON");
    }
    localStorage.removeItem("link_token");
  };

  const onFinicityEnrollSubmit = async () => {
    setIsLoading(true);
    const response = await vipApi.fetch("/api/finicity/enroll", "POST");
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
      const body = await response.json();
      if (body.status) {
        if (body.status === "success") {
          onEnrollSuccess();
        } else if (body.status === "noBanks") {
          await fetchSession(true);
          history.push({ pathname: "/addAccount", state: { nobank: true } });
        } else if (body.status === "failure") {
          onFailure(body);
        } else {
          onEnrollError(body);
        }
      } else {
        onEnrollError(body);
      }
    } else {
      onEnrollError("Fincity Response is not JSON");
    }
  };

  function onFailure(body) {
    faro.api.pushError(
      new Error(
        `Online Enrollment Failure - ${body.errorModel.patronErrorMessage ? body.errorModel.patronErrorMessage : body}`,
      ),
    );
    history.push({
      pathname: "/submit",
      state: {
        isEnrollment: true,
        isSuccess: false,
        isError: true,
        fromOperator,
        showHeader: false,
        allowRetry: false,
        onClickClose: true,
        title: "Account Not Created",
        message: `${body.errorModel.patronErrorMessage ? body.errorModel.patronErrorMessage : body}.`,
        customButtons: [
          {
            label: `Continue To ${session.operatorName}`,
            returnToOperator: true,
            returnUrl: session?.returnUrl,
            transactionId: session?.transactionId,
            transactionType:
              mode === MODE.manageBanks
                ? "bankManagement"
                : navigateToWithdraw
                  ? "withdraw"
                  : "deposit",
          },
        ],
      },
    });
  }

  async function onEnrollSuccess() {
    // Fetch updated session data - should now show as enrollment complete.
    await fetchSession(true);
    determineProductSwitchAndLimits();
    faro.api.pushEvent("Account Created", { session: session }, "session");
    history.push({
      pathname: "/submit",
      state: {
        isEnrollment: true,
        isSuccess: true,
        isError: false,
        title: "Account Created",
        message:
          "Your VIP account has been created and you are now able to make transactions.",
        navigateTo:
          mode === MODE.manageBanks
            ? undefined
            : navigateToDeposit
              ? "addFunds"
              : navigateToWithdraw
                ? "withdrawFunds"
                : "dashboard",
      },
    });
  }

  function onEnrollError(body) {
    faro.api.pushError(new Error("Account Not Created"), { context: body });
    history.push({
      pathname: "/submit",
      state: {
        isEnrollment: true,
        isSuccess: false,
        isError: true,
        onClickClose: true,
        title: "Account Not Created",
        message:
          "We are unable to complete this request. Please contact our Customer Service Team at 1-800-500-1973. Reference code E-28 so that we may better serve you.",
        buttonText: `Continue To ${session.operatorName}`,
      },
    });
  }

  const onExit = (error, metadata) => {
    window.sessionStorage.setItem("plaidlaunch", false);
    setDivStyle({ display: "block", width: "100%" });
    console.log(error, metadata);
    faro.api.pushEvent("User exited Plaid");
  };

  const showDiv = {
    display: "block",
  };

  const loadHandler = () => {
    if (!LastEnrollmentStep) {
      if (session?.toggles?.finicityEnabled && connectUrl) {
        faro.api.pushEvent("Launching Finicity");
        launchFinicity();
      } else {
        window.sessionStorage.setItem("plaidlaunch", true);
        const open = document.getElementsByClassName("ready")[0];
        if (open) {
          open.click();
          if (!plaidRedirect) {
            setIsLoading(false);
          }
          linkToken ?? faro.api.pushEvent("Launching Plaid");
        }
      }
    }
  };

  const launchFinicity = () => {
    const connectEventHandlers = {
      onDone: (event) => {
        console.log("onDone", event);
        faro.api.pushEvent("Enrolling with Finicity Account");
        setLastEnrollmentStep(true);
        setShowConfirmEnrollment(true);
      },
      onCancel: (event) => {
        setDivStyle({ display: "block", width: "100%" });
        console.log("onCancel", event);
        faro.api.pushEvent("User exited Finicity");
      },
      onError: (event) => {
        console.log("onError", event);
        onEnrollError("Finicity Launch Error");
      },
      onUser: ({ action, code, customerId }) => {
        if (action === "End" && code === 200 && customerId) {
          faro.api.pushEvent(
            "Add Finicity Account Success",
            { customerId: customerId },
            "session",
          );
        }
      },
      onLoad: () => {
        console.log("loaded");
      },
    };
    const connectOptions = {
      overlay: "rgba(199,201,199, 0.5);z-index: 10000;",
    };
    Connect.launch(connectUrl, connectEventHandlers, connectOptions);
  };

  let button;

  if (isLoading || !session) {
    button = <Loading />;
  } else {
    button = !isNative ? (
      <div style={divStyle}>
        {session.toggles.plaidEnabled && !connectUrl ? (
          <PlaidLink
            onEvent={(eventName, metadata) => {
              if (eventName === "OPEN") {
                faro.api.pushEvent("Enrollment - User clicked Plaid button");
                faro.api.pushLog(["Enrollment - User clicked Plaid button"], {
                  level: LogLevel.INFO,
                });
              }
            }}
            token={linkToken}
            onSuccess={onSuccessDefault}
            onExit={onExit}
            className="ready"
            onLoad={loadHandler}
            style={{
              width: "100%",
              border: "none",
              color: "#001d4a",
              textAlign: "center",
              background: "#3bc35c",
              letterSpacing: "-0.01188rem",
              padding: "var(--spacing-md)",
              gap: "var(--cta-size-medium)",
              fontSize: "var(--cta-size-medium)",
              borderRadius: "var(--spacing-5xl)",
              fontWeight: "var(--cta-font-weight-strong)",
            }}
          >
            Connect Your Bank Account
          </PlaidLink>
        ) : (
          <Finicity connectUrl={connectUrl} enrollmentStep={true} />
        )}
      </div>
    ) : (
      <Button className="ready" onClick={openNativeLink} fullWidth>
        Connect Your Bank Account
      </Button>
    );
  }

  let plaidFundingSourceError = (
    <>
      <StatusUpdateCard
        heading="Sorry!"
        body={
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <Typography>
              We are unable to add new accounts at this time.
            </Typography>
          </Box>
        }
        icon={<img src={lockClock} alt="Account Created Error"></img>}
      />
      <Button
        fullWidth
        buttonType="secondary"
        onClick={() => {
          history.push({
            pathname: "/addAccount",
            state: { nobank: true },
          });
        }}
      >
        Manage Funding Sources
      </Button>
    </>
  );

  return (
    <>
      {plaidRedirect && (
        <PlaidLink
          token={linkToken}
          onSuccess={onSuccessDefault}
          className="ready"
          onLoad={loadHandler}
          receivedRedirectUri={receivedRedirectUri}
          onExit={onExit}
        ></PlaidLink>
      )}
      {isLoading ? (
        <Loading></Loading>
      ) : (
        <>
          <div style={showDiv}>
            <Box
              className="header"
              sx={{ textAlign: isMobile ? "left" : "center" }}
              gap="var(--spacing-md)"
            >
              Account Sign Up
              <Typography sx={{ fontWeight: "bold" }}>
                {ssnIdentificationRequired
                  ? "Identification Info"
                  : "Add Funding Source"}
              </Typography>
            </Box>
          </div>
          {mitekRedirect ? (
            <div style={style}>
              <iframe
                src={mitekLink}
                allow="autoplay; camera; microphone"
                title="Verification Process"
                style={style}
              />
            </div>
          ) : (
            <>
              <Box pt=".75rem">
                {ssnIdentificationRequired ? (
                  <>
                    <Form id="ssnForm" onSubmit={onSaveSsn}>
                      <MaskedField
                        fieldName="ssn"
                        label="SSN#"
                        mask="999-99-9999"
                        maxLength={9}
                        required={true}
                        autoComplete="off"
                        validators={[Validators.SSN]}
                        onValueChanged={onFieldChange}
                        disabled={false}
                      />
                      <Button
                        fullWidth
                        type="submit"
                        sx={{ mt: 4 }}
                        className={
                          ssn
                            ? "submit-button click-enroll-next"
                            : "submit-button disabled"
                        }
                        disabled={!ssn}
                      >
                        Next
                      </Button>
                    </Form>
                  </>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {connectUrl || linkToken ? button : plaidFundingSourceError}
                  </Box>
                )}
              </Box>
              {showConfirmEnrollment && LastEnrollmentStep && (
                <Box>
                  <AddAccountSuccess />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      onClick={() => {
                        if (session.toggles.finicityEnabled && connectUrl) {
                          onFinicityEnrollSubmit();
                        } else {
                          onSubmitCommon();
                        }
                      }}
                      fullWidth
                      disabled={enrollmentFeeEnabled && !acceptedEnrollmentFee}
                    >
                      Submit
                    </Button>
                  </Box>
                </Box>
              )}
              {(connectUrl || linkToken) && (
                <CancelButton
                  btnText="Account Sign Up"
                  stage="Enrollment"
                ></CancelButton>
              )}
              {enrollmentFeeEnabled && LastEnrollmentStep && (
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "var(--caption-size-medium)",
                    marginTop: "var(--spacing-md)",
                    gap: "10px",
                  }}
                >
                  <Box className="enrollment-fee-checkbox">
                    <Checkbox
                      handleCheck={(checked) => {
                        setAcceptedEnrollmentFee(checked);
                      }}
                    />
                  </Box>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <p style={{ margin: 0 }}>
                      By checking this box, you agree to pay a $
                      {enrollmentFeeAmount?.split(".")[0]} enrollment fee.
                      Pavilion reserves the right to waive this fee at its sole
                      discretion.
                    </p>
                  </Box>
                </Box>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
export default OnlineEnrollment;
